import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Lock, 
  Eye, 
  Database,
  Server,
  UserCheck,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Building,
  Mail,
  Phone,
  Globe,
  FileText,
  Trash2,
  Settings
} from "lucide-react";
import { Button } from "../components/ui/button";

const PrivacyPolicy = () => {
  const lastUpdated = "7 de marzo de 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-navy-600 hover:text-navy-800 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver al inicio</span>
          </Link>
          <Link to="/">
            <span className="font-heading text-xl font-bold text-navy-900">Divídelo</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
          >
            <Shield className="w-5 h-5" />
            <span className="text-sm font-medium">Tu privacidad es importante</span>
          </motion.div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Política de Privacidad
          </h1>
          <p className="text-emerald-100 text-lg">
            Última actualización: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg prose-navy max-w-none">
          
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-emerald-800 mb-2">Nuestro Compromiso</h3>
                <p className="text-emerald-700 text-sm">
                  En Divídelo, proteger tu información personal es una prioridad. Esta política explica 
                  qué datos recopilamos, cómo los usamos y cómo los protegemos. Tu confianza es 
                  fundamental para nosotros.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
              <Database className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="font-semibold text-navy-900 text-sm">Datos que recopilamos</p>
                <p className="text-xs text-navy-500">Sección 2</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
              <Eye className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="font-semibold text-navy-900 text-sm">Cómo los usamos</p>
                <p className="text-xs text-navy-500">Sección 3</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="font-semibold text-navy-900 text-sm">Tus derechos</p>
                <p className="text-xs text-navy-500">Sección 7</p>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">1</span>
              Introducción
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>
                Esta Política de Privacidad describe cómo Divídelo ("nosotros", "nuestro" o "la plataforma") 
                recopila, utiliza, almacena y protege la información personal que nos proporcionas cuando 
                utilizas nuestros servicios de asesoría para procesos de visa.
              </p>
              <p>
                Al utilizar nuestros servicios, aceptas las prácticas descritas en esta política. Si no estás 
                de acuerdo con alguna parte de esta política, te pedimos que no utilices nuestros servicios.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">2</span>
              Información que Recopilamos
            </h2>
            <div className="pl-11 space-y-4 text-navy-700">
              <p><strong>2.1 Información que nos proporcionas directamente:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Datos de registro:</strong> Nombre completo, correo electrónico, número de teléfono, país de residencia.</li>
                <li><strong>Información para el proceso de visa:</strong> Datos personales necesarios para el formulario DS-160, incluyendo fecha de nacimiento, estado civil, historial laboral, historial de viajes, información familiar.</li>
                <li><strong>Documentos:</strong> Copias de pasaporte, fotografías, comprobantes de domicilio, estados de cuenta bancarios, y otros documentos que decidas subir a la plataforma.</li>
                <li><strong>Información de pago:</strong> Datos de tarjeta de crédito/débito (procesados de forma segura por nuestros proveedores de pago).</li>
                <li><strong>Comunicaciones:</strong> Mensajes enviados a través del chat, correos electrónicos y cualquier otra comunicación con nuestro equipo.</li>
              </ul>

              <p><strong>2.2 Información recopilada automáticamente:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Datos de uso:</strong> Páginas visitadas, tiempo en la plataforma, acciones realizadas, clics.</li>
                <li><strong>Información del dispositivo:</strong> Tipo de dispositivo, sistema operativo, navegador, dirección IP.</li>
                <li><strong>Cookies y tecnologías similares:</strong> Para mejorar tu experiencia y analizar el uso de la plataforma.</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-blue-700 text-sm">
                    <strong>Nota sobre datos sensibles:</strong> Algunos datos que recopilas para tu proceso de visa 
                    pueden considerarse información sensible. Estos datos son tratados con el más alto nivel de 
                    seguridad y solo se utilizan para el propósito específico de tu proceso migratorio.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">3</span>
              Cómo Utilizamos tu Información
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Utilizamos tu información personal para:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Proporcionar nuestros servicios:</strong> Procesar tu solicitud, brindarte asesoría, y guiarte en tu proceso de visa.</li>
                <li><strong>Comunicarnos contigo:</strong> Enviarte notificaciones, actualizaciones sobre tu caso, y responder a tus consultas.</li>
                <li><strong>Mejorar nuestros servicios:</strong> Analizar el uso de la plataforma para mejorar la experiencia del usuario.</li>
                <li><strong>Seguridad:</strong> Detectar y prevenir fraudes, abusos o actividades ilegales.</li>
                <li><strong>Cumplimiento legal:</strong> Cumplir con obligaciones legales y regulatorias aplicables.</li>
                <li><strong>Marketing:</strong> Enviarte información sobre nuestros servicios y promociones (solo con tu consentimiento).</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">4</span>
              Compartir Información con Terceros
            </h2>
            <div className="pl-11 space-y-4 text-navy-700">
              <p><strong>NO vendemos tu información personal.</strong> Solo compartimos tus datos en las siguientes circunstancias:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Proveedores de servicios:</strong> Empresas que nos ayudan a operar la plataforma (hosting, procesamiento de pagos, envío de correos).</li>
                <li><strong>Con tu consentimiento:</strong> Cuando nos autorizas expresamente a compartir información.</li>
                <li><strong>Requerimientos legales:</strong> Cuando sea requerido por ley, orden judicial o autoridad gubernamental.</li>
                <li><strong>Protección de derechos:</strong> Para proteger nuestros derechos, propiedad o seguridad, o los de otros usuarios.</li>
              </ul>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-emerald-700 text-sm">
                    Todos nuestros proveedores de servicios están obligados contractualmente a proteger tu 
                    información y solo pueden usarla para los fines específicos que les indicamos.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">5</span>
              Seguridad de la Información
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Implementamos medidas de seguridad técnicas y organizativas para proteger tu información:</p>
              
              <div className="grid sm:grid-cols-2 gap-4 my-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <Server className="w-6 h-6 text-emerald-600 mb-2" />
                  <p className="font-semibold text-navy-900 text-sm">Cifrado SSL/TLS</p>
                  <p className="text-xs text-navy-500">Toda la comunicación está encriptada</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Lock className="w-6 h-6 text-emerald-600 mb-2" />
                  <p className="font-semibold text-navy-900 text-sm">Datos encriptados</p>
                  <p className="text-xs text-navy-500">Información sensible cifrada en reposo</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Shield className="w-6 h-6 text-emerald-600 mb-2" />
                  <p className="font-semibold text-navy-900 text-sm">Acceso restringido</p>
                  <p className="text-xs text-navy-500">Solo personal autorizado</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Eye className="w-6 h-6 text-emerald-600 mb-2" />
                  <p className="font-semibold text-navy-900 text-sm">Monitoreo continuo</p>
                  <p className="text-xs text-navy-500">Detección de accesos no autorizados</p>
                </div>
              </div>

              <p className="text-sm text-navy-600">
                Sin embargo, ningún sistema es completamente seguro. Te recomendamos mantener tu contraseña 
                segura y no compartir tus credenciales de acceso.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">6</span>
              Retención de Datos
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Conservamos tu información personal durante el tiempo necesario para:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Proporcionar los servicios contratados.</li>
                <li>Cumplir con obligaciones legales y fiscales.</li>
                <li>Resolver disputas y hacer cumplir nuestros acuerdos.</li>
              </ul>
              <p>
                Generalmente, mantenemos los datos de tu cuenta durante <strong>5 años</strong> después de 
                finalizar tu proceso o de tu última actividad, a menos que la ley requiera un período diferente.
              </p>
              <p>
                Los documentos subidos a la plataforma pueden ser eliminados antes a tu solicitud.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">7</span>
              Tus Derechos
            </h2>
            <div className="pl-11 space-y-4 text-navy-700">
              <p>Tienes los siguientes derechos sobre tu información personal:</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                  <Eye className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy-900">Derecho de Acceso</p>
                    <p className="text-sm text-navy-600">Solicitar una copia de la información personal que tenemos sobre ti.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                  <Settings className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy-900">Derecho de Rectificación</p>
                    <p className="text-sm text-navy-600">Corregir información inexacta o incompleta.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                  <Trash2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy-900">Derecho de Eliminación</p>
                    <p className="text-sm text-navy-600">Solicitar la eliminación de tu información personal (sujeto a obligaciones legales).</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                  <Globe className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy-900">Derecho de Portabilidad</p>
                    <p className="text-sm text-navy-600">Recibir tus datos en un formato estructurado y de uso común.</p>
                  </div>
                </div>
              </div>

              <p className="mt-4">
                Para ejercer cualquiera de estos derechos, contáctanos a <a href="mailto:info@dividelo.com" className="text-emerald-600 hover:underline">info@dividelo.com</a>. 
                Responderemos a tu solicitud en un plazo máximo de 30 días.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">8</span>
              Cookies y Tecnologías de Seguimiento
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Utilizamos cookies y tecnologías similares para:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cookies esenciales:</strong> Necesarias para el funcionamiento de la plataforma.</li>
                <li><strong>Cookies de preferencias:</strong> Para recordar tus configuraciones y preferencias.</li>
                <li><strong>Cookies analíticas:</strong> Para entender cómo usas la plataforma y mejorar nuestros servicios.</li>
              </ul>
              <p>
                Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar la funcionalidad 
                de la plataforma.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">9</span>
              Menores de Edad
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>
                Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionalmente 
                información de menores. Si eres padre o tutor y crees que tu hijo nos ha proporcionado 
                información personal, contáctanos para eliminarla.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">10</span>
              Cambios a esta Política
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>
                Podemos actualizar esta política periódicamente. Te notificaremos sobre cambios significativos 
                mediante un aviso en la plataforma o por correo electrónico. Te recomendamos revisar esta 
                política regularmente.
              </p>
              <p>
                La fecha de "última actualización" al inicio de esta página indica cuándo se realizó la 
                última modificación.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 text-sm font-bold">11</span>
              Contacto
            </h2>
            <div className="pl-11 space-y-4 text-navy-700">
              <p>Si tienes preguntas sobre esta política de privacidad o sobre cómo manejamos tus datos, contáctanos:</p>
              <div className="bg-slate-50 rounded-xl p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-navy-500" />
                  <span><strong>Divídelo</strong> - Responsable de Protección de Datos</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-navy-500" />
                  <a href="mailto:privacidad@dividelo.com" className="text-emerald-600 hover:underline">privacidad@dividelo.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-navy-500" />
                  <a href="https://wa.me/19548059212" className="text-emerald-600 hover:underline">+1 (954) 805-9212</a>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/terminos">
            <Button variant="outline" className="gap-2">
              <FileText className="w-4 h-4" />
              Ver Términos y Condiciones
            </Button>
          </Link>
          <Link to="/">
            <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-navy-300 text-sm">
            © {new Date().getFullYear()} Divídelo. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
